import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, Bot, User, Sparkles, MessageSquare, Plus, Clock } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const CopilotIA = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadSessions();
    startNewSession();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await api.get('/ai/copilot/sessions');
      if (response.data.success) {
        setSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    }
  };

  const startNewSession = () => {
    const newSessionId = `copilot-${Date.now()}`;
    setSessionId(newSessionId);
    setMessages([{
      role: 'assistant',
      content: `Bonjour ! Je suis votre assistant IA Baobab Connect amélioré. 🚀

## 🎯 Mes Capacités Avancées

**🔍 Recherche Avancée:**
• Trouver des opérations par critères complexes (poids, dates, villes, statut)
• Chercher des chauffeurs disponibles avec filtres
• Recherche multi-paramètres

**➕ Création d'Opérations Complète:**
• Créer des opérations avec +50 champs supportés
• Support de TOUS les paramètres (marchandise, conteneur, client, tarifs, dates, documents)
• Assignation automatique de chauffeur

**📊 Rapports & Analyses:**
• Rapports mensuels/hebdomadaires complets
• Comparaisons de périodes
• **Analyse financière poussée** (rentabilité, tendances, prédictions, alertes)

**💵 Pricing Intelligent:**
• **NOUVEAU: Calcul de tarifs intelligent selon algorithme Baobab**
• Tarifs client (bas/moyen/haut) + zone transporteur
• Comparaison avec historique
• Prise en compte: tonnage, distance, urgence, retour vide, type marchandise

**⚡ Actions en Masse:**
• Mise à jour de statuts en masse
• Recommandations intelligentes

**💰 Exemples de Commandes:**
• "Calcule le tarif pour 35 tonnes Abidjan vers Daloa retour vide"
• "Quel est le prix pour 20 pieds de 25T Yopougon → Bouaké urgent ?"
• "Analyse financière des 30 derniers jours"
• "Crée une opération complète: 20 tonnes vers Bouaké, client SODECI, tarif 500000 FCFA, conteneur 40 pieds"
• "Quels sont les trajets les plus rentables ce mois ?"

Comment puis-je vous aider aujourd'hui ?`,
      timestamp: new Date().toISOString()
    }]);
  };

  const loadSession = async (sid) => {
    try {
      const response = await api.get(`/ai/copilot/history?session_id=${sid}&limit=50`);
      if (response.data.success) {
        const history = response.data.history.reverse();
        const formattedMessages = history.flatMap(h => [
          { role: 'user', content: h.message, timestamp: h.timestamp },
          { role: 'assistant', content: h.response, timestamp: h.timestamp }
        ]);
        setMessages(formattedMessages);
        setSessionId(sid);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      toast.error('Erreur lors du chargement de la conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Ajouter le message de l'utilisateur
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    setIsLoading(true);

    try {
      const response = await api.post('/ai/copilot/chat', {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: response.data.timestamp
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error(response.data.message || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de la communication avec l\'IA';
      toast.error(errorMessage);
      
      // Ajouter un message d'erreur
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Désolé, une erreur s'est produite : ${errorMessage}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Bot className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Copilote IA Baobab Connect</h1>
                  <p className="text-blue-100 text-sm">Assistant intelligent pour l'analyse de transport</p>
                </div>
              </div>
              <button
                onClick={startNewSession}
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nouvelle conversation</span>
                <span className="sm:hidden">Nouveau</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 h-[calc(100vh-200px)] sm:h-[calc(100vh-250px)]">
            {/* Sidebar - Sessions */}
            <div className="hidden lg:block lg:col-span-3 bg-gray-50 border-r border-gray-200 overflow-y-auto">
              <div className="p-4">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Historique</h3>
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <button
                      key={session.session_id}
                      onClick={() => loadSession(session.session_id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        sessionId === session.session_id
                          ? 'bg-emerald-50 border-2 border-emerald-500'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <MessageSquare className="h-4 w-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700 truncate">
                          Session {new Date(session.last_message).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-slate-500">
                        <Clock className="h-3 w-3" />
                        <span>{formatTime(session.last_message)}</span>
                        <span>•</span>
                        <span>{session.message_count} messages</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="col-span-1 lg:col-span-9 flex flex-col min-h-0">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex space-x-3 max-w-3xl ${
                        message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                      }`}
                    >
                      {/* Avatar */}
                      <div
                        className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                          message.role === 'user'
                            ? 'bg-emerald-600'
                            : message.isError
                            ? 'bg-red-500'
                            : 'bg-gradient-to-br from-emerald-500 to-emerald-700'
                        }`}
                      >
                        {message.role === 'user' ? (
                          <User className="h-5 w-5 text-white" />
                        ) : (
                          <Sparkles className="h-5 w-5 text-white" />
                        )}
                      </div>

                      {/* Message */}
                      <div
                        className={`rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
                          message.role === 'user'
                            ? 'bg-emerald-600 text-white'
                            : message.isError
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="text-xs sm:text-sm whitespace-pre-wrap">{message.content}</p>
                        {message.timestamp && (
                          <p
                            className={`text-xs mt-1 ${
                              message.role === 'user' ? 'text-emerald-100' : 'text-gray-500'
                            }`}
                          >
                            {formatTime(message.timestamp)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex space-x-2 sm:space-x-3 max-w-3xl">
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <div className="bg-gray-100 rounded-2xl px-3 sm:px-4 py-2 sm:py-3">
                        <div className="flex items-center space-x-2">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
                          <p className="text-xs sm:text-sm text-gray-600">L'IA réfléchit...</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
                <form onSubmit={sendMessage} className="flex space-x-2 sm:space-x-3">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Posez une question..."
                    disabled={isLoading}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !inputMessage.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl transition-colors flex items-center space-x-2 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>Envoyer</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Suggestions Avancées */}
                <div className="mt-2 sm:mt-3">
                  <p className="text-xs text-gray-500 mb-2">Actions rapides :</p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {/* Recherche */}
                    <button
                      onClick={() => setInputMessage('Trouve les opérations de plus de 15 tonnes vers Bouaké ce mois')}
                      disabled={isLoading}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-blue-200"
                    >
                      🔍 Recherche avancée
                    </button>
                    
                    {/* Création */}
                    <button
                      onClick={() => setInputMessage('Crée une opération pour livrer 10 tonnes à Yamoussoukro demain')}
                      disabled={isLoading}
                      className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-emerald-200"
                    >
                      ➕ Créer opération
                    </button>
                    
                    {/* Recommandation */}
                    <button
                      onClick={() => setInputMessage('Recommande le meilleur chauffeur disponible pour un trajet Abidjan-Bouaké')}
                      disabled={isLoading}
                      className="text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-purple-200"
                    >
                      ⭐ Recommandation
                    </button>
                    
                    {/* Rapports */}
                    <button
                      onClick={() => setInputMessage('Génère le rapport mensuel de novembre 2025')}
                      disabled={isLoading}
                      className="text-xs bg-orange-50 hover:bg-orange-100 text-orange-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-orange-200"
                    >
                      📊 Rapport mensuel
                    </button>
                    
                    {/* Actions en masse */}
                    <button
                      onClick={() => setInputMessage('Passe toutes les opérations terminées de cette semaine en statut Facturé')}
                      disabled={isLoading}
                      className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-indigo-200"
                    >
                      ⚡ Action en masse
                    </button>
                    
                    {/* Analyse */}
                    <button
                      onClick={() => setInputMessage('Analyse les performances du mois dernier comparé à ce mois')}
                      disabled={isLoading}
                      className="text-xs bg-pink-50 hover:bg-pink-100 text-pink-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-pink-200"
                    >
                      📈 Comparaison
                    </button>
                    
                    {/* Analyse Financière */}
                    <button
                      onClick={() => setInputMessage('Analyse financière complète des 30 derniers jours avec recommandations')}
                      disabled={isLoading}
                      className="text-xs bg-green-50 hover:bg-green-100 text-green-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-green-200"
                    >
                      💰 Analyse Financière
                    </button>
                    
                    {/* Calcul Tarif */}
                    <button
                      onClick={() => setInputMessage('Calcule le tarif pour 25 tonnes Abidjan vers Bouaké')}
                      disabled={isLoading}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors disabled:opacity-50 border border-blue-200"
                    >
                      💵 Calcul Tarif
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopilotIA;
